/* Desktop slide-out drawer — single shared drawer with tabs */
(function () {
  'use strict';

  class HeaderSlideDrawer extends HTMLElement {
    constructor() {
      super();
      this.triggers = Array.from(this.querySelectorAll('[data-slide-drawer-open]'));
      this.overlay = this.querySelector('[data-slide-drawer-overlay]');
      this.panel = this.overlay ? this.overlay.querySelector('[data-slide-drawer-panel-wrap]') : null;
      this.lastFocused = null;
      this.closeToken = 0;

      this._onTriggerClick = this.onTriggerClick.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onKey = this.onKey.bind(this);
    }

    connectedCallback() {
      this.triggers.forEach((btn) => btn.addEventListener('click', this._onTriggerClick));
      this.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
    }

    onTriggerClick(e) {
      if (!this.overlay) return;
      const btn = e.currentTarget;
      const tab = btn.getAttribute('data-target-tab');
      e.preventDefault();
      const isOpen = this.overlay.classList.contains('is-open');
      const currentTab = this.overlay.getAttribute('data-active-tab') || '';
      if (isOpen && currentTab === tab) {
        this.close();
      } else if (isOpen) {
        this.switchTab(tab);
      } else {
        this.switchTab(tab, { silent: true });
        this.open(btn);
      }
    }

    onDocClick(e) {
      if (e.target.closest('[data-slide-drawer-close]') || e.target.closest('[data-slide-drawer-backdrop]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const tabBtn = e.target.closest('[data-slide-drawer-tab]');
      if (tabBtn && this.overlay && this.overlay.contains(tabBtn)) {
        e.preventDefault();
        const tab = tabBtn.getAttribute('data-tab-target') || '';
        this.switchTab(tab);
        return;
      }
      const reveal = e.target.closest('[data-slide-drawer-reveal]');
      if (reveal && this.overlay && this.overlay.contains(reveal)) {
        e.preventDefault();
        this.revealSub(reveal);
        return;
      }
      const link = e.target.closest('a.slide-drawer__row, a.slide-drawer__top-link');
      if (link && window.reasnAnalytics) {
        const inDrawer = !!link.closest('[data-slide-drawer-overlay]');
        const event = inDrawer ? 'drawer_link_clicked' : 'nav_link_clicked';
        const props = {
          surface: 'desktop',
          label: (link.textContent || '').trim(),
          href: link.getAttribute('href') || ''
        };
        if (inDrawer && this.overlay) {
          props.tab = this.overlay.getAttribute('data-active-tab') || '';
          props.depth = link.classList.contains('slide-drawer__row--sub') ? 3 : 2;
        }
        window.reasnAnalytics.track(event, props);
      }
    }

    onKey(e) {
      if (e.key === 'Escape' && this.overlay && this.overlay.classList.contains('is-open')) {
        e.preventDefault();
        this.close();
      }
    }

    open(trigger) {
      if (!this.overlay) return;
      this.closeToken++;
      this.lastFocused = trigger || document.activeElement;
      this.overlay.hidden = false;
      void this.overlay.offsetWidth;
      this.overlay.classList.add('is-open');
      this.syncTriggerStates();
      document.documentElement.style.overflow = 'hidden';
      const focusTarget = this.overlay.querySelector('[data-slide-drawer-close]');
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 50);
      if (window.reasnAnalytics) {
        window.reasnAnalytics.track('drawer_opened', {
          surface: 'desktop',
          tab: this.overlay.getAttribute('data-active-tab') || ''
        });
      }
    }

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-open');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';

      const myToken = ++this.closeToken;
      const finish = () => {
        if (myToken !== this.closeToken) return;
        if (this.overlay.classList.contains('is-open')) return;
        this.resetSubs();
        this.overlay.hidden = true;
      };

      if (this.panel) {
        const onEnd = (ev) => {
          if (ev && ev.target !== this.panel) return;
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        };
        this.panel.addEventListener('transitionend', onEnd);
        setTimeout(() => {
          this.panel.removeEventListener('transitionend', onEnd);
          finish();
        }, 600);
      } else {
        finish();
      }

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    resetSubs() {
      if (!this.overlay) return;
      this.overlay.classList.remove('has-sub');
      this.overlay.querySelectorAll('[data-slide-drawer-sub]').forEach((s) => {
        s.hidden = true;
      });
      this.overlay.querySelectorAll('[data-slide-drawer-reveal]').forEach((r) => {
        r.classList.remove('is-active');
        r.setAttribute('aria-expanded', 'false');
      });
    }

    revealSub(button) {
      if (!this.overlay) return;
      const subId = button.getAttribute('data-target-sub');
      if (window.reasnAnalytics) {
        window.reasnAnalytics.track('drawer_sub_revealed', {
          surface: 'desktop',
          tab: this.overlay.getAttribute('data-active-tab') || '',
          parent: (button.textContent || '').trim()
        });
      }
      this.overlay.querySelectorAll('[data-slide-drawer-sub]').forEach((s) => {
        s.hidden = s.dataset.subId !== subId;
      });
      this.overlay.querySelectorAll('[data-slide-drawer-reveal]').forEach((r) => {
        const isThis = r === button;
        r.classList.toggle('is-active', isThis);
        r.setAttribute('aria-expanded', isThis ? 'true' : 'false');
      });
      this.overlay.classList.add('has-sub');
    }

    switchTab(tab, opts) {
      if (!this.overlay || !tab) return;
      const previous = this.overlay.getAttribute('data-active-tab') || '';
      this.overlay.setAttribute('data-active-tab', tab);
      if (!opts || !opts.silent) {
        if (window.reasnAnalytics && previous !== tab) {
          window.reasnAnalytics.track('drawer_tab_switched', {
            surface: 'desktop',
            from: previous,
            to: tab
          });
        }
      }
      this.overlay.querySelectorAll('[data-slide-drawer-tab]').forEach((btn) => {
        const isThis = btn.getAttribute('data-tab-target') === tab;
        btn.classList.toggle('is-active', isThis);
        btn.setAttribute('aria-selected', isThis ? 'true' : 'false');
      });
      this.overlay.querySelectorAll('[data-slide-drawer-pane]').forEach((pane) => {
        pane.hidden = pane.getAttribute('data-pane-tab') !== tab;
      });
      this.resetSubs();
      this.syncTriggerStates();
    }

    syncTriggerStates() {
      if (!this.overlay) return;
      const isOpen = this.overlay.classList.contains('is-open');
      const activeTab = this.overlay.getAttribute('data-active-tab') || '';
      this.triggers.forEach((t) => {
        const isThis = t.getAttribute('data-target-tab') === activeTab;
        t.setAttribute('aria-expanded', isOpen && isThis ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('header-slide-drawer')) {
    customElements.define('header-slide-drawer', HeaderSlideDrawer);
  }
})();
